import { Alert, Badge } from '@vivaahealth/design-system';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as api from '../../api';

import { Button } from '../../components/Button';
import { CheckMark } from '../../components/CheckMark';
import { InfoMessage } from '../../components/InfoMessage';
import { LinkText } from '../../components/LinkText';

import defaultStyles from '../../styles/formSetStyles.module.css';
import {
  isAppointmentCancellableNow,
  getCancelTimeWindow,
} from '../../utils/confirmCancel';
import * as PhoneNumber from '../../utils/phoneNumber';
import { DoctorAppointmentProfileView } from './DoctorAppointmentProfileView';
import styles from './index.module.css';

interface Props {
  appointments: api.NotificationIntent.Appointment[];
  onCancelAppointments: (encounterIds: string[]) => void;
  pending?: boolean;
}

interface ReviewProps {
  appointment: api.NotificationIntent.Appointment;
  checked: boolean;
  onCheck: (value: boolean) => void;
  onContinue: () => void;
  onCancel: () => void;
}

export const ReviewAppointment: React.FC<ReviewProps> = ({
  appointment,
  checked,
  onCheck,
  onContinue,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { encounter, provider, practice, virtualVisit } = appointment;
  return (
    <>
      <div className={classNames(styles.CancelDialog, styles.Bold)}>
        {t(
          'Review the requirements for cancelling appointments within {{time}}:',
          { time: getCancelTimeWindow(appointment, t) },
        )}
      </div>

      <DoctorAppointmentProfileView
        key={encounter.id}
        id={encounter.id}
        startTime={encounter.startTime}
        arrivalTime={encounter.arrivalTime || undefined}
        status={encounter.status || undefined}
        provider={provider}
        practice={practice}
        virtualVisit={virtualVisit}
        hideEncounterStartTimeByVisitKind={
          appointment.preferences.hideEncounterStartTimeByVisitKind
        }
        kindOfVisit={appointment.encounter.kindOfVisit || undefined}
      >
        <InfoMessage className={styles.MessageNoMargin} variant="warning">
          <Trans
            i18nKey="Call {{practiceName}} at <link /> to complete this cancellation."
            defaults="Call {{practiceName}} at <link /> to complete this cancellation."
            values={{ practiceName: practice.name }}
            components={{
              link: (
                <LinkText
                  to={`tel:${practice.phoneNumber}`}
                  title={PhoneNumber.prettify(practice.phoneNumber)}
                />
              ),
            }}
          />
        </InfoMessage>
      </DoctorAppointmentProfileView>
      <CheckMark
        className={styles.AcknowledgeCheckbox}
        label={t(
          'I acknowledge that this appointment will not be canceled until I call the clinic number listed with the appointment',
        )}
        checked={checked}
        onClick={() => {
          onCheck(!checked);
        }}
      />
      <Button
        type="button"
        disabled={!checked}
        onClick={onContinue}
        children={t('Acknowledge and submit')}
      />
      <Button
        type="button"
        invertColors
        children={t('Nevermind, keep appointment')}
        onClick={onCancel}
      />
    </>
  );
};

/**
 * Display a list of selectable appointment profile, allow cancelling selected ones
 */
export const CancelAppointmentListView: React.FC<Props> = ({
  appointments,
  onCancelAppointments,
  pending,
}) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [idsToReview, setIdsToReview] = useState<string[]>([]);
  const [idsReviewed, setIdsReviewed] = useState<string[]>([]);
  const [currentReviewId, setCurrentReviewId] = useState<string>();

  const onReviewCheck = (value: boolean, encounterId: string) => {
    value
      ? setIdsReviewed([...idsReviewed, encounterId])
      : setIdsReviewed([...idsReviewed.filter((id) => id !== encounterId)]);
  };

  const onReviewContinue = (encounterId: string) => {
    if (idsToReview.length === 1) {
      return onCancelAppointments(selectedIds);
    }

    setCurrentReviewId(idsToReview[1]);
    setIdsToReview([...idsToReview.filter((id) => id !== encounterId)]);
  };

  const onReviewCancel = (encounterId: string) => {
    const selectedIdsAfterDenial = [
      ...selectedIds.filter((id) => id !== encounterId),
    ];
    setSelectedIds(selectedIdsAfterDenial);

    if (idsToReview.length === 1) {
      setCurrentReviewId(undefined);
      setIdsToReview([]);
      selectedIdsAfterDenial.length &&
        onCancelAppointments(selectedIdsAfterDenial);
      return;
    }
    setCurrentReviewId(idsToReview[1]);
    setIdsToReview([...idsToReview.filter((id) => id !== encounterId)]);
  };

  const selectedAppointmentsCount = selectedIds.length;

  const reviewAppointment =
    currentReviewId &&
    appointments.find((appt) => appt.encounter.id === currentReviewId);
  if (reviewAppointment) {
    return (
      <ReviewAppointment
        appointment={reviewAppointment}
        checked={idsReviewed.includes(reviewAppointment.encounter.id)}
        onCheck={(value) =>
          onReviewCheck(value, reviewAppointment.encounter.id)
        }
        onContinue={() => onReviewContinue(reviewAppointment.encounter.id)}
        onCancel={() => onReviewCancel(reviewAppointment.encounter.id)}
      />
    );
  }

  return (
    <>
      <label className={classNames(defaultStyles.Label, styles.Label)}>
        {t('Select the appointment you would like to cancel.')}
      </label>
      <div className={styles.AppointmentsList}>
        {appointments
          .filter((appointment) => appointment.isCancellable)
          .map((appointment) => {
            const isCancellableNow = isAppointmentCancellableNow(appointment);
            const { encounter, provider, practice, virtualVisit, preferences } =
              appointment;
            return (
              <DoctorAppointmentProfileView
                key={encounter.id}
                id={encounter.id}
                startTime={encounter.startTime}
                arrivalTime={encounter.arrivalTime || undefined}
                status={encounter.status || undefined}
                provider={provider}
                practice={practice}
                virtualVisit={virtualVisit}
                hideEncounterStartTimeByVisitKind={
                  preferences.hideEncounterStartTimeByVisitKind
                }
                kindOfVisit={encounter.kindOfVisit || undefined}
                onSelect={(id) => {
                  setSelectedIds([...selectedIds, id]);
                  if (!isCancellableNow) setIdsToReview([...idsToReview, id]);
                }}
                onDeselect={(targetId) => {
                  setSelectedIds([
                    ...selectedIds.filter((id) => id !== targetId),
                  ]);
                  setIdsToReview([
                    ...idsToReview.filter((id) => id !== targetId),
                  ]);
                }}
                selectable
                badge={
                  !isCancellableNow ? (
                    <Badge
                      children={t('Within {{time}}', {
                        time: getCancelTimeWindow(appointment, t),
                      })}
                      className={styles.DoctorProfileBadge}
                      variant="danger"
                    />
                  ) : undefined
                }
              >
                {!isCancellableNow && (
                  <Alert
                    className={styles.MessageNoMargin}
                    variant="danger"
                    title={t('Cancellation window has passed')}
                  >
                    {t(
                      'Cancelling appointments within {{time}} will require a call to the clinic listed above.',
                      { time: getCancelTimeWindow(appointment, t) },
                    )}
                  </Alert>
                )}
              </DoctorAppointmentProfileView>
            );
          })}
      </div>
      <Button
        type="button"
        onClick={() => {
          if (idsToReview.length) {
            return setCurrentReviewId(idsToReview[0]);
          }
          onCancelAppointments(selectedIds);
        }}
        children={
          <Trans
            i18nKey="cancelAppointment"
            count={selectedAppointmentsCount}
          />
        }
        disabled={selectedAppointmentsCount === 0 || pending}
      />
    </>
  );
};
